import { AddValueBox } from "./addValueBox";
import PlusIcon from "../../assets/plusIconGreen.svg";
import { useEffect, useRef, useState } from "react";
import { formatPrice } from "../../utils/formatPrice";
import { convertToFormattedAmount } from "../../utils/convertAmount";
import { useDownPaymentStore } from "../../stores/downPaymentDetailsStore";
import { useUserMortgageProfileStore } from "../../stores/userProfileStore";
import { DownPaymentSourceMapping } from "../../interfaces/mortgageDetailsInterface";

const DownPaymentDetailsView = () => {
  const [openBox, setOpenBox] = useState<string | null>(null);
  const [initialRender, setInitialRender] = useState(true);
  const { userMortgageProfile } = useUserMortgageProfileStore();

  const {
    sources,
    addSource,
    removeSource,
    updateAmount,
    isValidationTriggered,
  } = useDownPaymentStore();

  const reverseMapping = Object.fromEntries(
    Object.entries(DownPaymentSourceMapping).map(([key, value]) => [value, key])
  );

  const handleAddSource = (sourceLabel: string) => {
    const enumSource = DownPaymentSourceMapping[sourceLabel];
    if (enumSource) {
      addSource(enumSource);
      setOpenBox(null);
      setInitialRender(false);
    }
  };

  const handleDelete = (title: string) => {
    const enumSource = DownPaymentSourceMapping[title];
    if (enumSource) {
      removeSource(enumSource);
    }
  };

  const handleAmountChange = (title: string, newAmount: string) => {
    const enumSource = DownPaymentSourceMapping[title];
    if (enumSource) {
      updateAmount(enumSource, newAmount);
    }
  };

  const downPaymentOptions = Object.keys(DownPaymentSourceMapping);

  const downPaymentSum = sources.reduce(
    (total, { amount }) => total + (Number.parseFloat(amount) || 0),
    0
  );
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && userMortgageProfile?.downPaymentSources) {
      const validSources = userMortgageProfile.downPaymentSources.map(
        ({ type }) => type
      );

      for (const { type, amount } of userMortgageProfile.downPaymentSources) {
        const existingSource = sources.find((s) => s.source === type);
        if (existingSource) {
          updateAmount(type, convertToFormattedAmount(amount.amount).toString());
        } else {
          addSource(type);
          updateAmount(type, convertToFormattedAmount(amount.amount).toString());
        }
      }

      for (const { source } of sources) {
        if (!validSources.includes(source)) {
          removeSource(source);
        }
      }

      initialized.current = true;
    }
  }, [userMortgageProfile, sources, addSource, updateAmount, removeSource]);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="font-bold text-2xl">
          Where Will Your Down Payment Come From?
        </div>
        <div className="text-gray-500">
          Please include both yours and your co-applicants savings combined
          total down payment contribution
        </div>
      </div>
      <hr className="bg-[#6B7280]" />

      {sources.map(({ source, amount }) => (
        <AddValueBox
          key={source}
          title={reverseMapping[source]}
          error={
            isValidationTriggered && amount.trim() === ""
              ? "Amount is required"
              : ""
          }
          amount={amount}
          onAmountChange={(newAmount) =>
            handleAmountChange(reverseMapping[source], newAmount)
          }
          onDelete={() => handleDelete(reverseMapping[source])}
        />
      ))}

      {initialRender || openBox === "AddOptions" ? (
        <div className="space-y-2">
          {downPaymentOptions
            .filter(
              (option) =>
                !sources.some(
                  (s) => DownPaymentSourceMapping[option] === s.source
                )
            )
            .map((option) => (
              <div
                key={option}
                onClick={() => handleAddSource(option)}
                onKeyDown={() => handleAddSource(option)}
                className="border border-[#E5E7EB] rounded-[8px] flex items-center gap-2 font-medium p-5 cursor-pointer"
              >
                <img src={PlusIcon} alt="plus icon" />
                {option}
              </div>
            ))}
        </div>
      ) : (
        sources.length < downPaymentOptions.length && (
          <div
            onClick={() => setOpenBox("AddOptions")}
            onKeyDown={() => setOpenBox("AddOptions")}
            className="border border-[#E5E7EB] rounded-[8px] flex items-center gap-2 font-medium p-5 cursor-pointer"
          >
            <img src={PlusIcon} alt="plus icon" />
            Add Another Down Payment Source
          </div>
        )
      )}

      {downPaymentSum > 0 && (
        <div className="border border-[#E5E7EB] rounded-[8px] flex flex-col gap-2 font-medium p-5">
          <div className="font-bold text-2xl">
            ${formatPrice(downPaymentSum)}
          </div>
          <div className="font-medium text-base">Total Down Payment</div>
        </div>
      )}
    </div>
  );
};

export { DownPaymentDetailsView };
